import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  CssBaseline,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CircleIcon from '@mui/icons-material/Circle';
import UsernameDialog from './components/UsernameDialog';
import UserList from './components/UserList';
import ChatPanel from './components/ChatPanel';
import FilesPanel from './components/FilesPanel';
import SettingsDialog from './components/SettingsDialog';
import CreateGroupDialog from './components/CreateGroupDialog';
import InviteUserDialog from './components/InviteUserDialog';
import JoinTeamDialog from './components/JoinOrganizationDialog';
import JoinContactDialog from './components/JoinContactDialog';
import { authAPI, usersAPI, messagesAPI, serverAPI, groupsAPI, userFilesAPI, invitesAPI } from './api';
import { parseInviteFromSearchParams } from './utils/inviteCode';
import { io } from 'socket.io-client';
import { getBackendUrl } from './utils/apiConfig';
import { usersCanSeeEachOther } from './utils/userVisibility';
import './App.css';

function ShareLoginConfirmDialog({ prompt, error, loading, onConfirm, onCancel }) {
  const open = !!prompt;
  const username = prompt?.user?.username;
  const alreadyUsed = prompt?.alreadyUsed;
  const canConfirm = !!prompt?.user && !alreadyUsed && !error;
  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Confirm Login</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
        ) : alreadyUsed ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            This share-login link has already been used. Ask the sender to generate a new one.
          </Alert>
        ) : username ? (
          <>
            <Typography variant="body1" sx={{ mt: 1 }}>
              You are about to log in as <strong>{username}</strong>.
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will replace any existing login in this browser.
            </Alert>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={!canConfirm || loading}
        >
          {loading ? <CircularProgress size={18} /> : `Log in as ${username || ''}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function App() {
  const savedSidebarTabRaw = localStorage.getItem('sidebarTab');
  const savedSidebarTab = Number.parseInt(savedSidebarTabRaw ?? '', 10);
  const initialSidebarTab = Number.isInteger(savedSidebarTab) && savedSidebarTab >= 0 && savedSidebarTab <= 2
    ? savedSidebarTab
    : 0;
  const initialSelectedFolderId = localStorage.getItem('selectedFolderId');

  const [token, setToken] = useState(localStorage.getItem('token'));
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [socket, setSocket] = useState(null);
  const [serverOnline, setServerOnline] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [inviteUserOpen, setInviteUserOpen] = useState(false);
  const [joinTeamOpen, setJoinTeamOpen] = useState(false);
  const [joinContactOpen, setJoinContactOpen] = useState(false);
  const [pendingInviterUsername, setPendingInviterUsername] = useState(null);
  const [pendingInviteDigits, setPendingInviteDigits] = useState(null);
  const [pendingInvitePreview, setPendingInvitePreview] = useState(null);
  const [urlInviteInviter, setUrlInviteInviter] = useState(null);
  const [urlInviteDigits, setUrlInviteDigits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastReadTimestamps, setLastReadTimestamps] = useState(() => {
    // Load from localStorage
    const stored = localStorage.getItem('lastReadTimestamps');
    return stored ? JSON.parse(stored) : {};
  });
  const [typingUsers, setTypingUsers] = useState(new Set()); // Track which users are typing
  const selectedUserIdRef = useRef(selectedUserId);
  const selectedGroupIdRef = useRef(selectedGroupId);
  const currentUserRef = useRef(currentUser);
  const groupsRef = useRef(groups);
  const pendingOnlineUserIdsRef = useRef(null);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [isTabFocused, setIsTabFocused] = useState(true);
  const isTabFocusedRef = useRef(true);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [shareLoginPrompt, setShareLoginPrompt] = useState(null);
  const [shareLoginError, setShareLoginError] = useState(null);
  const [redeemingShareLogin, setRedeemingShareLogin] = useState(false);
  const [sidebarTab, setSidebarTab] = useState(initialSidebarTab); // 0=users, 1=groups, 2=files
  const [injectedAttachment, setInjectedAttachment] = useState(null); // { key, attachments }
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(initialSelectedFolderId);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [foldersLoadedOnce, setFoldersLoadedOnce] = useState(false);
  const [folderViewSource, setFolderViewSource] = useState('mine');
  const [sharedContacts, setSharedContacts] = useState([]);
  const [filesEpoch, setFilesEpoch] = useState(0);

  // Parse share-login token from URL and show confirmation dialog
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shareToken = urlParams.get('shareLogin');
    if (!shareToken) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await authAPI.previewShareLogin(shareToken);
        if (cancelled) return;
        const { user, alreadyUsed } = response.data;
        setShareLoginPrompt({ shareToken, user, alreadyUsed });
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to preview share-login token:', error);
        setShareLoginError(
          error.response?.data?.error || 'Invalid or expired share-login link.'
        );
        setShareLoginPrompt({ shareToken, user: null, alreadyUsed: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleConfirmShareLogin = async () => {
    if (!shareLoginPrompt?.shareToken) return;
    setRedeemingShareLogin(true);
    setShareLoginError(null);
    try {
      const response = await authAPI.redeemShareLogin(shareLoginPrompt.shareToken);
      const { token: newToken } = response.data;
      localStorage.setItem('token', newToken);
      // Clean the shareLogin query param out of the URL before reloading
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete('shareLogin');
      const cleanSearch = urlParams.toString();
      const newUrl =
        window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
      // Reload to fully reset app state with the new login
      window.location.reload();
    } catch (error) {
      console.error('Failed to redeem share-login token:', error);
      setShareLoginError(
        error.response?.data?.error || 'Failed to log in with this link.'
      );
      setRedeemingShareLogin(false);
    }
  };

  const handleCancelShareLogin = () => {
    setShareLoginPrompt(null);
    setShareLoginError(null);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('shareLogin');
    const cleanSearch = urlParams.toString();
    const newUrl =
      window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash;
    window.history.replaceState({}, document.title, newUrl);
  };

  const clearInviteUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('inviter');
    urlParams.delete('code');
    urlParams.delete('invite');
    const cleanSearch = urlParams.toString();
    const newUrl =
      window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash;
    window.history.replaceState({}, document.title, newUrl);
  };

  const handleInviteFromUrl = async (parsed, loggedInUser) => {
    if (!parsed?.inviterUsername || !parsed.inviteDigits) return;

    setUrlInviteInviter(parsed.inviterUsername);
    setUrlInviteDigits(parsed.inviteDigits);

    try {
      const response = await invitesAPI.preview(parsed.inviterUsername, parsed.inviteDigits);
      const preview = response.data;
      setPendingInvitePreview(preview);
      setPendingInviterUsername(parsed.inviterUsername);
      setPendingInviteDigits(parsed.inviteDigits);

      if (loggedInUser) {
        if (preview.type === 'contact_invite') {
          const contactIds = loggedInUser.contactIds || [];
          if (!contactIds.includes(preview.invitedBy)) {
            setJoinContactOpen(true);
          }
        } else if (preview.type === 'team_invite') {
          const userTeamIds = loggedInUser.teamIds || [];
          if (!userTeamIds.includes(preview.teamId)) {
            setJoinTeamOpen(true);
          }
        }
        clearInviteUrlParams();
      }
    } catch (error) {
      console.error('Failed to preview invite from URL:', error);
    }
  };

  // Parse invite code from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const parsed = parseInviteFromSearchParams(urlParams);
    if (!parsed) return;

    if (currentUser) {
      handleInviteFromUrl(parsed, currentUser);
    } else {
      setUrlInviteInviter(parsed.inviterUsername);
      setUrlInviteDigits(parsed.inviteDigits);
    }
  }, [token, currentUser]);

  // Initialize: Check for token or show username dialog
  useEffect(() => {
    const initialize = async () => {
      if (token) {
        try {
          const response = await authAPI.getMe();
          setCurrentUser(response.data.user);
          setLoading(false);
          
          const urlParams = new URLSearchParams(window.location.search);
          const parsed = parseInviteFromSearchParams(urlParams);
          if (parsed) {
            await handleInviteFromUrl(parsed, response.data.user);
          }
        } catch (error) {
          // Token invalid, clear it
          localStorage.removeItem('token');
          setToken(null);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    initialize();
  }, [token]);

  // Update refs when values change
  useEffect(() => {
    selectedUserIdRef.current = selectedUserId;
  }, [selectedUserId]);

  useEffect(() => {
    selectedGroupIdRef.current = selectedGroupId;
  }, [selectedGroupId]);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission);
        });
      } else {
        setNotificationPermission(Notification.permission);
      }
    }
  }, []);

  // Track tab focus/blur for notifications
  useEffect(() => {
    const handleFocus = () => {
      setIsTabFocused(true);
      isTabFocusedRef.current = true;
    };
    const handleBlur = () => {
      setIsTabFocused(false);
      isTabFocusedRef.current = false;
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Track tab visibility for away status
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsTabVisible(isVisible);
      
      // Emit away/back status when tab visibility changes
      if (socket && currentUser) {
        if (isVisible) {
          socket.emit('user:back');
        } else {
          socket.emit('user:away');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [socket, currentUser]);

  // Setup WebSocket connection and load users
  useEffect(() => {
    if (!currentUser || !token) return;

    let newSocket = null;
    let isMounted = true;
    
    const setupSocket = async () => {
      const API_URL = await getBackendUrl();
      if (!isMounted) return;
      
      newSocket = io(API_URL, {
        auth: { token },
      });

      // Set up event handlers BEFORE connecting
      // Store online user IDs to apply when users are loaded
      pendingOnlineUserIdsRef.current = null;

      newSocket.on('users:online', (onlineUserIds) => {
        console.log('Received users:online event:', onlineUserIds);
        pendingOnlineUserIdsRef.current = onlineUserIds;
        setUsers(prevUsers => {
          if (prevUsers.length === 0) {
            // Users not loaded yet, will apply when they are loaded
            return prevUsers;
          }
          return prevUsers.map(user => ({
            ...user,
            online: onlineUserIds.includes(user.id),
          }));
        });
      });

      newSocket.on('users:away', (awayUserIds) => {
        console.log('Received users:away event:', awayUserIds);
        setUsers(prevUsers => {
          if (prevUsers.length === 0) {
            return prevUsers;
          }
          return prevUsers.map(user => ({
            ...user,
            away: awayUserIds.includes(user.id),
          }));
        });
      });

      newSocket.on('connect', async () => {
        console.log('Connected to server');
        setServerOnline(true);
        
        // Emit away status if tab is not visible when connecting
        if (currentUser) {
          if (document.hidden) {
            // Tab is not visible, user is away
            newSocket.emit('user:away');
          }
          // If tab is visible, user is online and not away (default state)
        }
        
        // Load initial user list and groups when connected
        try {
          const [usersResponse, groupsResponse] = await Promise.all([
            usersAPI.getAll(),
            groupsAPI.getAll()
          ]);
          let usersData = usersResponse.data;
          
          // Apply pending online status if we received it before loading users
          if (pendingOnlineUserIdsRef.current) {
            usersData = usersData.map(user => ({
              ...user,
              online: pendingOnlineUserIdsRef.current.includes(user.id),
              away: user.away || false, // Preserve away status if present
            }));
            pendingOnlineUserIdsRef.current = null;
          }
          
          setUsers(usersData);
          setGroups(groupsResponse.data);
        } catch (error) {
          console.error('Failed to load users/groups:', error);
        }
      });

      newSocket.on('disconnect', () => {
        console.log('Disconnected from server');
        setServerOnline(false);
        // Mark current user as offline in UI (they'll be marked online again on reconnect)
      });

      newSocket.on('reconnect', () => {
        console.log('Reconnected to server');
        setServerOnline(true);
        // Reload users to sync online status after reconnection
        usersAPI.getAll()
          .then(response => {
            let usersData = response.data;
            // Apply pending online status if available
            if (pendingOnlineUserIdsRef.current) {
              usersData = usersData.map(user => ({
                ...user,
                online: pendingOnlineUserIdsRef.current.includes(user.id),
                away: user.away || false, // Preserve away status if present
              }));
              pendingOnlineUserIdsRef.current = null;
            }
            setUsers(usersData);
          })
          .catch(error => console.error('Failed to reload users:', error));
      });

      newSocket.on('connect_error', () => {
        setServerOnline(false);
      });

      newSocket.on('user:online', ({ userId, username }) => {
        const hasTeams = (currentUser?.teamIds || []).length > 0;
        const hasContacts = (currentUser?.contactIds || []).length > 0;
        if (!hasTeams && !hasContacts) {
          return;
        }
        
        setUsers(prevUsers => {
          // Check if user already exists in the list
          const userExists = prevUsers.some(u => u.id === userId);
          if (userExists) {
            // Update existing user's online status
            return prevUsers.map(user =>
              user.id === userId ? { ...user, online: true, away: user.away || false } : user
            );
          } else {
            // Add new user if they don't exist (shouldn't happen often, but handle it)
            // Note: UserList component will filter by teams as a safety measure
            return [...prevUsers, { id: userId, username, online: true, away: false }];
          }
        });
      });

      newSocket.on('user:offline', ({ userId }) => {
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === userId ? { ...user, online: false, away: false } : user
          )
        );
      });

      newSocket.on('user:away', ({ userId }) => {
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === userId ? { ...user, away: true } : user
          )
        );
      });

      newSocket.on('user:back', ({ userId }) => {
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === userId ? { ...user, away: false } : user
          )
        );
      });

      newSocket.on('user:usernameChanged', ({ userId, username }) => {
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === userId ? { ...user, username } : user
          )
        );
        if (currentUser.id === userId) {
          setCurrentUser(prev => ({ ...prev, username }));
        }
      });

      newSocket.on('user:profilePictureChanged', ({ userId, profilePictureUrl }) => {
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.id === userId ? { ...user, profilePictureUrl } : user
          )
        );
        if (currentUser.id === userId) {
          setCurrentUser(prev => ({ ...prev, profilePictureUrl }));
        }
      });

      newSocket.on('user:teamsChanged', async ({ userId }) => {
        if (currentUser.id === userId) {
          try {
            const meResponse = await authAPI.getMe();
            setCurrentUser(meResponse.data.user);
          } catch (error) {
            console.error('Failed to refresh current user after team change:', error);
          }
        }
        try {
          const response = await usersAPI.getAll();
          setUsers(response.data);
        } catch (error) {
          console.error('Failed to reload users after team change:', error);
        }
      });

      newSocket.on('user:new', ({ user }) => {
        if (!usersCanSeeEachOther(currentUser, user)) {
          return;
        }
        setUsers(prevUsers => {
          const userExists = prevUsers.some(u => u.id === user.id);
          if (!userExists) {
            return [...prevUsers, { ...user, online: false, away: false }];
          }
          return prevUsers;
        });
      });

      newSocket.on('users:deleted', ({ userIds }) => {
        // Handle user deletions
        setUsers(prevUsers => prevUsers.filter(user => !userIds.includes(user.id)));
        // Clear selection if selected user was deleted
        setSelectedUserId(prevId => {
          if (prevId && userIds.includes(prevId)) {
            return null;
          }
          return prevId;
        });
      });

      newSocket.on('group:new', ({ group }) => {
        // Handle new group creation
        setGroups(prevGroups => {
          const groupExists = prevGroups.some(g => g.id === group.id);
          if (!groupExists) {
            return [...prevGroups, group];
          }
          return prevGroups;
        });
      });

      newSocket.on('message:new', (message) => {
        setMessages(prev => [...prev, message]);
        
        // If the message is in the currently open conversation, mark it as read
        const currentSelectedUserId = selectedUserIdRef.current;
        const currentSelectedGroupId = selectedGroupIdRef.current;
        const currentUserValue = currentUserRef.current;
        const currentGroups = groupsRef.current;
        
        if (currentUserValue) {
          let conversationId = null;
          let isCurrentConversation = false;
          
          if (currentSelectedUserId) {
            conversationId = [currentUserValue.id, currentSelectedUserId].sort().join('::');
            isCurrentConversation = message.conversationId === conversationId;
          } else if (currentSelectedGroupId && message.type === 'group') {
            // Check if it's a group message in the currently selected group
            const selectedGroup = currentGroups.find(g => g.id === currentSelectedGroupId);
            if (selectedGroup && selectedGroup.conversationId === message.conversationId) {
              conversationId = message.conversationId;
              isCurrentConversation = true;
            }
          }
          
          // Mark as read if in current conversation and not from current user
          if (isCurrentConversation && message.senderId !== currentUserValue.id) {
            const now = new Date().toISOString();
            setLastReadTimestamps(prev => {
              const updated = { ...prev, [conversationId]: now };
              localStorage.setItem('lastReadTimestamps', JSON.stringify(updated));
              return updated;
            });
          }

          // Show notification if:
          // 1. Message is not from current user
          // 2. Notifications are enabled in settings
          // 3. Notification permission is granted
          // 4. Either tab is not visible (hidden) OR tab is not focused OR message is not in current conversation
          const notificationsEnabled = localStorage.getItem('notificationsEnabled') !== 'false';
          if (
            message.senderId !== currentUserValue.id &&
            notificationsEnabled &&
            'Notification' in window &&
            Notification.permission === 'granted' &&
            (document.hidden || !isTabFocusedRef.current || !isCurrentConversation)
          ) {
            // Get sender name
            let senderName = message.senderName || 'Someone';
            if (message.type === 'group') {
              // For group messages, include group name if available
              const group = currentGroups.find(g => g.conversationId === message.conversationId);
              const groupName = group ? group.name : 'Group';
              senderName = `${senderName} (${groupName})`;
            }

            // Create notification body text
            let bodyText = '';
            if (message.fileAttachment) {
              bodyText = `Sent ${message.fileAttachment.fileName}`;
            } else if (message.message) {
              // Strip HTML tags for notification
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = message.message;
              bodyText = tempDiv.textContent || tempDiv.innerText || '';
              // Truncate if too long
              if (bodyText.length > 100) {
                bodyText = bodyText.substring(0, 100) + '...';
              }
            }

            // Show notification with error handling for Edge compatibility
            try {
              const notificationOptions = {
                body: bodyText || 'New message',
                icon: '/favicon.ico',
                tag: message.conversationId, // Group notifications by conversation
                requireInteraction: false,
              };

              // Badge is not supported in all browsers (e.g., Edge)
              if ('badge' in Notification.prototype) {
                notificationOptions.badge = '/favicon.ico';
              }

              const notification = new Notification(`${senderName}`, notificationOptions);

              // Close notification after 5 seconds
              setTimeout(() => {
                if (notification) {
                  notification.close();
                }
              }, 5000);

              // Optional: Focus window when notification is clicked
              notification.onclick = () => {
                window.focus();
                if (notification) {
                  notification.close();
                }
              };

              // Handle notification errors
              notification.onerror = (error) => {
                console.error('Notification error:', error);
              };
            } catch (error) {
              console.error('Failed to create notification:', error);
            }
          }
        }
      });

      // Handle message updates (edits) - no notification
      newSocket.on('message:updated', (updatedMessage) => {
        setMessages(prev => prev.map(msg => 
          msg.id === updatedMessage.id ? updatedMessage : msg
        ));
      });

      // Handle message deletions
      newSocket.on('message:deleted', (deletedMessage) => {
        setMessages(prev => prev.map(msg => 
          msg.id === deletedMessage.id ? deletedMessage : msg
        ));
      });

      // Handle typing indicators
      newSocket.on('typing:start', (data) => {
        const currentUserValue = currentUserRef.current;
        if (!currentUserValue) return;
        
        // Check if the typing is in a conversation with the current user
        const conversationId = [currentUserValue.id, data.userId].sort().join('::');
        if (data.conversationId === conversationId && data.userId !== currentUserValue.id) {
          setTypingUsers(prev => new Set([...prev, data.userId]));
        }
      });

      newSocket.on('typing:stop', (data) => {
        const currentUserValue = currentUserRef.current;
        if (!currentUserValue) return;
        
        // Check if the typing stop is in a conversation with the current user
        const conversationId = [currentUserValue.id, data.userId].sort().join('::');
        if (data.conversationId === conversationId) {
          setTypingUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.userId);
            return newSet;
          });
        }
      });

      // Check initial connection status
      if (newSocket.connected) {
        setServerOnline(true);
      }

      setSocket(newSocket);
    };

    setupSocket();

    return () => {
      isMounted = false;
      if (newSocket) {
        newSocket.close();
        setServerOnline(false);
      }
    };
  }, [currentUser, token]);

  // Load messages when user or group is selected
  useEffect(() => {
    if (!currentUser) return;

    const loadMessages = async () => {
      try {
        let conversationId;
        let type;
        
        if (selectedUserId) {
          conversationId = [currentUser.id, selectedUserId].sort().join('::');
          type = 'direct';
        } else if (selectedGroupId) {
          const group = groups.find(g => g.id === selectedGroupId);
          if (group) {
            conversationId = group.conversationId;
            type = 'group';
          } else {
            return;
          }
        } else {
          return;
        }

        const response = await messagesAPI.getMessages(conversationId, type);
        setMessages(response.data);
        
        // Mark messages as read when conversation is opened
        const now = new Date().toISOString();
        setLastReadTimestamps(prev => {
          const updated = { ...prev, [conversationId]: now };
          localStorage.setItem('lastReadTimestamps', JSON.stringify(updated));
          return updated;
        });
      } catch (error) {
        console.error('Failed to load messages:', error);
      }
    };

    loadMessages();
  }, [currentUser, selectedUserId, selectedGroupId, groups]);

  const handleUsernameSubmit = async (username, inviterUsername, inviteDigits) => {
    try {
      const response = await authAPI.register(username, inviterUsername, inviteDigits);
      const { token: newToken, user } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setCurrentUser(user);

      setPendingInviterUsername(null);
      setPendingInviteDigits(null);
      setPendingInvitePreview(null);
      setUrlInviteInviter(null);
      setUrlInviteDigits(null);
      clearInviteUrlParams();
      
      if ((user.teamIds && user.teamIds.length > 0) || (user.contactIds && user.contactIds.length > 0)) {
        try {
          const usersResponse = await usersAPI.getAll();
          setUsers(usersResponse.data);
        } catch (error) {
          console.error('Failed to reload users:', error);
        }
      }
    } catch (error) {
      console.error('Failed to register:', error);
      alert(error.response?.data?.error || 'Failed to register. Please try again.');
    }
  };

  const handleLoginWithCode = async (code) => {
    try {
      const response = await authAPI.loginWithCode(code);
      const { token: newToken, user } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setCurrentUser(user);
    } catch (error) {
      console.error('Failed to log in with code:', error);
      alert(error.response?.data?.error || 'Failed to log in. Please try again.');
    }
  };

  const handleSendMessage = (messageData) => {
    if (socket) {
      socket.emit('message:send', messageData);
    }
  };

  const handleCreateGroup = async (name, userIds) => {
    try {
      const response = await groupsAPI.create(name, userIds);
      // Don't manually add to state - the socket event 'group:new' will handle it
      // This prevents duplicate entries when the socket event arrives
      // Select the newly created group - it will be added to the list via socket event
      setSelectedGroupId(response.data.id);
      setSelectedUserId(null);
    } catch (error) {
      console.error('Failed to create group:', error);
      alert('Failed to create group. Please try again.');
    }
  };

  const handleHideGroup = async (groupId) => {
    if (window.confirm('Are you sure you want to hide this group chat? You can unhide it later if needed.')) {
      try {
        await groupsAPI.hide(groupId);
        // Remove the group from the local state
        setGroups(prev => prev.filter(g => g.id !== groupId));
        // Clear selection if this group was selected
        if (selectedGroupId === groupId) {
          setSelectedGroupId(null);
        }
      } catch (error) {
        console.error('Failed to hide group:', error);
        alert('Failed to hide group. Please try again.');
      }
    }
  };

  const handleUpdateUsername = async (userId, newUsername) => {
    try {
      await usersAPI.updateUsername(userId, newUsername);
      // The socket event will update the UI
    } catch (error) {
      console.error('Failed to update username:', error);
      throw error;
    }
  };

  const handleUsersDeleted = (deletedUserIds) => {
    // Remove deleted users from the users list
    setUsers(prev => prev.filter(user => !deletedUserIds.includes(user.id)));
    // Clear selection if selected user was deleted
    if (selectedUserId && deletedUserIds.includes(selectedUserId)) {
      setSelectedUserId(null);
    }
  };

  const handleRestartServer = async () => {
    if (window.confirm('Are you sure you want to restart the backend server?')) {
      try {
        await serverAPI.shutdown();
        // Server will restart automatically via the auto-restart script
      } catch (error) {
        // This is expected - the server closes the connection when shutting down
        console.log('Server shutdown initiated');
      }
    }
  };

  const loadFolders = async () => {
    setFoldersLoading(true);
    try {
      const sharedBy = folderViewSource === 'mine' ? null : folderViewSource;
      const response = await userFilesAPI.getFolders(sharedBy);
      setFolders(response.data || []);
    } catch (err) {
      console.error('Failed to load folders', err);
    } finally {
      setFoldersLoading(false);
      setFoldersLoadedOnce(true);
    }
  };

  const loadSharedContacts = async () => {
    try {
      const response = await userFilesAPI.getSharedContacts();
      setSharedContacts(response.data || []);
    } catch (err) {
      console.error('Failed to load shared contacts', err);
    }
  };

  useEffect(() => {
    if (!currentUser) return;
    loadFolders();
    loadSharedContacts();
  }, [currentUser, folderViewSource]);

  const handleFolderViewChange = (source) => {
    setFolderViewSource(source);
    setSelectedFolderId(null);
  };

  const handleFilesMoved = () => {
    setFilesEpoch((n) => n + 1);
  };

  const handleUpdateFolderSharing = async (folderId, userIds, writeUserIds) => {
    await userFilesAPI.updateFolderSharing(folderId, userIds, writeUserIds);
    await loadFolders();
    await loadSharedContacts();
  };

  useEffect(() => {
    localStorage.setItem('sidebarTab', String(sidebarTab));
  }, [sidebarTab]);

  useEffect(() => {
    if (selectedFolderId) {
      localStorage.setItem('selectedFolderId', selectedFolderId);
    } else {
      localStorage.removeItem('selectedFolderId');
    }
  }, [selectedFolderId]);

  useEffect(() => {
    if (!selectedFolderId || foldersLoading || !foldersLoadedOnce) return;
    const matchedFolder = folders.find((folder) => String(folder.id) === String(selectedFolderId));
    if (!matchedFolder) {
      setSelectedFolderId(null);
      return;
    }
    // Normalize restored IDs to the same runtime type as folder.id values.
    if (matchedFolder.id !== selectedFolderId) {
      setSelectedFolderId(matchedFolder.id);
    }
  }, [folders, foldersLoading, selectedFolderId]);

  const handleCreateFolder = async (name, parentId = null) => {
    const response = await userFilesAPI.createFolder(name, parentId);
    await loadFolders();
    return response.data;
  };

  const handleRenameFolder = async (folderId, name) => {
    await userFilesAPI.renameFolder(folderId, name);
    await loadFolders();
  };

  const handleMoveFolder = async (folderId, parentId) => {
    await userFilesAPI.moveFolder(folderId, parentId);
    await loadFolders();
  };

  const handleDeleteFolder = async (folderId) => {
    await userFilesAPI.deleteFolder(folderId);
    if (selectedFolderId === folderId) setSelectedFolderId(null);
    await loadFolders();
  };

  const handleSendFileToChat = (target, attachments) => {
    if (target.type === 'direct') {
      setSelectedUserId(target.user.id);
      setSelectedGroupId(null);
    } else if (target.type === 'group') {
      setSelectedGroupId(target.group.id);
      setSelectedUserId(null);
    }
    setSidebarTab(target.type === 'direct' ? 0 : 1);
    setInjectedAttachment({ key: Date.now(), attachments });
  };

  const selectedUser = users.find(u => u.id === selectedUserId);
  const selectedGroup = groups.find(g => g.id === selectedGroupId);
  
  let conversationId = null;
  let conversationType = null;
  if (selectedUserId) {
    conversationId = [currentUser?.id, selectedUserId].sort().join('::');
    conversationType = 'direct';
  } else if (selectedGroupId && selectedGroup) {
    conversationId = selectedGroup.conversationId;
    conversationType = 'group';
  }

  if (loading) {
    return <Box>Loading...</Box>;
  }

  if (!currentUser) {
    return (
      <>
        <UsernameDialog 
          open={!shareLoginPrompt}
          onSubmit={handleUsernameSubmit}
          onLoginWithCode={handleLoginWithCode}
          initialInviterUsername={urlInviteInviter}
          initialInviteDigits={urlInviteDigits}
        />
        <ShareLoginConfirmDialog
          prompt={shareLoginPrompt}
          error={shareLoginError}
          loading={redeemingShareLogin}
          onConfirm={handleConfirmShareLogin}
          onCancel={handleCancelShareLogin}
        />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Chat App
          </Typography>
          <Tooltip title={serverOnline ? 'Server Online' : 'Server Offline'}>
            <IconButton color="inherit" sx={{ cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}>
              <CircleIcon 
                sx={{ 
                  fontSize: 12, 
                  color: serverOnline ? '#4caf50' : '#f44336',
                  filter: serverOnline ? 'drop-shadow(0 0 4px rgba(76, 175, 80, 0.6))' : 'none'
                }} 
              />
            </IconButton>
          </Tooltip>
          <IconButton color="inherit" onClick={handleRestartServer} title="Restart Backend Server">
            <RestartAltIcon />
          </IconButton>
          <IconButton color="inherit" onClick={() => setSettingsOpen(true)}>
            <SettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: 300,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 300,
            boxSizing: 'border-box',
            mt: '64px',
          },
        }}
      >
        <UserList
          users={users}
          messages={messages}
          currentUserId={currentUser.id}
          selectedUserId={selectedUserId}
          onSelectUser={(userId) => {
            setSelectedUserId(userId);
            setSelectedGroupId(null);
          }}
          lastReadTimestamps={lastReadTimestamps}
          typingUsers={typingUsers}
          groups={groups}
          selectedGroupId={selectedGroupId}
          onSelectGroup={(groupId) => {
            setSelectedGroupId(groupId);
            setSelectedUserId(null);
          }}
          onCreateGroupClick={() => setCreateGroupOpen(true)}
          onInviteUserClick={() => setInviteUserOpen(true)}
          currentUser={currentUser}
          activeTab={sidebarTab}
          onTabChange={setSidebarTab}
          folders={folders}
          foldersLoading={foldersLoading}
          selectedFolderId={selectedFolderId}
          onSelectFolder={setSelectedFolderId}
          onCreateFolder={handleCreateFolder}
          onRenameFolder={handleRenameFolder}
          onMoveFolder={handleMoveFolder}
          onDeleteFolder={handleDeleteFolder}
          folderViewSource={folderViewSource}
          onFolderViewChange={handleFolderViewChange}
          sharedContacts={sharedContacts}
          onUpdateFolderSharing={handleUpdateFolderSharing}
          onRefreshFolders={loadFolders}
          onFilesMoved={handleFilesMoved}
        />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
        }}
      >
        {sidebarTab === 2 ? (
          <FilesPanel
            currentUser={currentUser}
            users={users}
            groups={groups}
            onSendFileToChat={handleSendFileToChat}
            folders={folders}
            selectedFolderId={selectedFolderId}
            onSelectFolder={setSelectedFolderId}
            onCreateFolder={handleCreateFolder}
            folderViewSource={folderViewSource}
            filesEpoch={filesEpoch}
          />
        ) : (
          <ChatPanel
            currentUser={currentUser}
            selectedUser={selectedUser}
            selectedGroup={selectedGroup}
            messages={messages}
            onSendMessage={handleSendMessage}
            conversationId={conversationId}
            conversationType={conversationType}
            socket={socket}
            onHideGroup={handleHideGroup}
            users={users}
            injectedAttachment={injectedAttachment}
            onInjectedAttachmentConsumed={() => setInjectedAttachment(null)}
          />
        )}
      </Box>

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        currentUser={currentUser}
        onUpdateUsername={handleUpdateUsername}
        onUsersDeleted={handleUsersDeleted}
        onTeamUpdate={async () => {
          // Refresh user data to get updated teams
          try {
            const response = await authAPI.getMe();
            setCurrentUser(response.data.user);
            // Reload users to show users from shared teams
            const usersResponse = await usersAPI.getAll();
            setUsers(usersResponse.data);
          } catch (error) {
            console.error('Failed to refresh user data:', error);
          }
        }}
      />

      <CreateGroupDialog
        open={createGroupOpen}
        onClose={() => setCreateGroupOpen(false)}
        users={users}
        currentUserId={currentUser?.id}
        onCreateGroup={handleCreateGroup}
      />

      {currentUser && (
        <InviteUserDialog
          open={inviteUserOpen}
          onClose={() => setInviteUserOpen(false)}
          currentUser={currentUser}
        />
      )}

      {currentUser && (
        <JoinTeamDialog
          open={joinTeamOpen}
          onClose={() => {
            setJoinTeamOpen(false);
            setPendingInviterUsername(null);
            setPendingInviteDigits(null);
            setPendingInvitePreview(null);
          }}
          inviterUsername={pendingInviterUsername}
          inviteDigits={pendingInviteDigits}
          preview={pendingInvitePreview}
          onJoinSuccess={() => {
            authAPI.getMe().then(response => {
              setCurrentUser(response.data.user);
              usersAPI.getAll().then(usersResponse => {
                setUsers(usersResponse.data);
              });
            });
          }}
        />
      )}

      {currentUser && (
        <JoinContactDialog
          open={joinContactOpen}
          onClose={() => {
            setJoinContactOpen(false);
            setPendingInviterUsername(null);
            setPendingInviteDigits(null);
            setPendingInvitePreview(null);
          }}
          inviterUsername={pendingInviterUsername}
          inviteDigits={pendingInviteDigits}
          preview={pendingInvitePreview}
          onJoinSuccess={() => {
            authAPI.getMe().then(response => {
              setCurrentUser(response.data.user);
              usersAPI.getAll().then(usersResponse => {
                setUsers(usersResponse.data);
              });
            });
          }}
        />
      )}

      <ShareLoginConfirmDialog
        prompt={shareLoginPrompt}
        error={shareLoginError}
        loading={redeemingShareLogin}
        onConfirm={handleConfirmShareLogin}
        onCancel={handleCancelShareLogin}
      />
    </Box>
  );
}

export default App;
